import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Users, ChefHat, Heart, AlertCircle, Loader, Building2, MapPin, DollarSign, Beer } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';
import DateInput from '../components/DateInput';

const CreateBlindDate = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [venues, setVenues] = useState<any[]>([]);
  const [selectedVenueType, setSelectedVenueType] = useState<'restaurant' | 'bar'>('restaurant');
  const [selectedVenueId, setSelectedVenueId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    spots_total: 2,
    spots_available: 2,
    price_range: '',
    age_range: '',
    preferred_gender: '',
    dining_style: '',
    description: ''
  });

  useEffect(() => {
    fetchVenues();
  }, []);

  const fetchVenues = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) {
        navigate('/login');
        return;
      }

      // Fetch approved restaurants
      const { data: restaurants, error: restaurantsError } = await supabase
        .from('restaurants')
        .select('*')
        .eq('status', 'approved');

      if (restaurantsError) {
        console.error('Error fetching restaurants:', restaurantsError);
        throw restaurantsError;
      }

      // Fetch approved bars
      const { data: bars, error: barsError } = await supabase
        .from('bars')
        .select('*')
        .eq('status', 'approved');

      if (barsError) {
        console.error('Error fetching bars:', barsError);
        throw barsError;
      }

      // Combine venues
      const allVenues = [
        ...(restaurants || []).map(r => ({ ...r, type: 'restaurant' as const })),
        ...(bars || []).map(b => ({ ...b, type: 'bar' as const }))
      ];

      setVenues(allVenues);
      
      if (allVenues.length === 0) {
        setError(t('blindDates.noVenuesFound'));
      }
    } catch (err) {
      console.error('Error fetching venues:', err);
      setError(err instanceof Error ? err.message : t('blindDates.fetchVenuesError'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'spots_total') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        spots_available: value // Update spots_available to match spots_total
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleDateChange = (date: string) => {
    setFormData(prev => ({
      ...prev,
      date
    }));
  };

  const handleVenueTypeChange = (type: 'restaurant' | 'bar') => {
    setSelectedVenueType(type);
    setSelectedVenueId(null); // Reset selected venue when changing type
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVenueId) {
      setError(t('blindDates.selectVenueError'));
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error(t('common.loginRequired'));

      // Create the blind date
      const insertData = {
        host_id: user.id,
        date: formData.date,
        time: formData.time,
        spots_total: parseInt(formData.spots_total.toString()),
        spots_available: parseInt(formData.spots_available.toString()),
        price_range: formData.price_range,
        age_range: formData.age_range,
        preferred_gender: formData.preferred_gender,
        dining_style: formData.dining_style,
        description: formData.description,
        status: 'open'
      };

      // Add the appropriate venue ID based on selected type
      if (selectedVenueType === 'restaurant') {
        Object.assign(insertData, { restaurant_id: selectedVenueId });
      } else {
        Object.assign(insertData, { bar_id: selectedVenueId });
      }

      const { error: insertError } = await supabase
        .from('blind_dates')
        .insert(insertData);

      if (insertError) throw insertError;

      // Navigate to the events page
      navigate('/my-events');
    } catch (err) {
      console.error('Error creating blind date:', err);
      setError(err instanceof Error ? err.message : t('blindDates.createError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error && venues.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="flex items-center space-x-2 mb-6">
          <Heart className="h-6 w-6 text-pink-500" />
          <h2 className="text-2xl font-bold text-gray-900">
            {t('blindDates.createTitle')}
          </h2>
        </div>

        {error && (
          <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Venue Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('blindDates.venueType')}
            </label>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => handleVenueTypeChange('restaurant')}
                className={`flex-1 py-3 px-6 rounded-lg font-medium transition ${
                  selectedVenueType === 'restaurant'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <ChefHat className="h-5 w-5" />
                  <span>{t('venue.register.restaurant')}</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => handleVenueTypeChange('bar')}
                className={`flex-1 py-3 px-6 rounded-lg font-medium transition ${
                  selectedVenueType === 'bar'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Beer className="h-5 w-5" />
                  <span>{t('venue.register.bar')}</span>
                </div>
              </button>
            </div>
          </div>

          {/* Venue Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {selectedVenueType === 'restaurant' ? t('blindDates.selectRestaurant') : t('blindDates.selectBar')}
            </label>
            <select
              value={selectedVenueId || ''}
              onChange={(e) => setSelectedVenueId(e.target.value)}
              className="w-full p-2 border rounded-md"
              required
            >
              <option value="">{t('blindDates.selectVenue')}</option>
              {venues
                .filter(venue => venue.type === selectedVenueType)
                .map(venue => (
                  <option key={venue.id} value={venue.id}>
                    {venue.name} - {venue.address}
                  </option>
                ))}
            </select>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <DateInput
                label={t('common.date')}
                value={formData.date}
                onChange={handleDateChange}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4" />
                  <span>{t('common.time')}</span>
                </div>
              </label>
              <input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4" />
                  <span>{t('blindDates.groupSize')}</span>
                </div>
              </label>
              <input
                type="number"
                name="spots_total"
                value={formData.spots_total}
                onChange={handleInputChange}
                min="2"
                max="8"
                className="w-full p-2 border rounded-md"
                placeholder={t('blindDates.numberOfPeople')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <DollarSign className="h-4 w-4" />
                  <span>{t('search.priceRange')}</span>
                </div>
              </label>
              <select
                name="price_range"
                value={formData.price_range}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              >
                <option value="">{t('blindDates.selectPriceRange')}</option>
                <option value="$">{t('blindDates.priceRanges.budget')}</option>
                <option value="$$">{t('blindDates.priceRanges.moderate')}</option>
                <option value="$$$">{t('blindDates.priceRanges.upscale')}</option>
                <option value="$$$$">{t('blindDates.priceRanges.luxury')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4" />
                  <span>{t('blindDates.ageRange')}</span>
                </div>
              </label>
              <select
                name="age_range"
                value={formData.age_range}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              >
                <option value="">{t('blindDates.selectAgeRange')}</option>
                <option value="18-25">18-25</option>
                <option value="26-35">26-35</option>
                <option value="36-45">36-45</option>
                <option value="46-55">46-55</option>
                <option value="56+">{t('blindDates.ageRanges.over56')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4" />
                  <span>{t('blindDates.genderPreference')}</span>
                </div>
              </label>
              <select
                name="preferred_gender"
                value={formData.preferred_gender}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              >
                <option value="">{t('blindDates.selectGenderPreference')}</option>
                <option value="any">{t('blindDates.genderPreferences.any')}</option>
                <option value="male">{t('blindDates.genderPreferences.male')}</option>
                <option value="female">{t('blindDates.genderPreferences.female')}</option>
                <option value="mixed">{t('blindDates.genderPreferences.mixed')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center space-x-2">
                  <ChefHat className="h-4 w-4" />
                  <span>{t('blindDates.diningStyle')}</span>
                </div>
              </label>
              <select
                name="dining_style"
                value={formData.dining_style}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              >
                <option value="">{t('blindDates.selectDiningStyle')}</option>
                <option value="casual">{t('blindDates.diningStyles.casual')}</option>
                <option value="formal">{t('blindDates.diningStyles.formal')}</option>
                <option value="business">{t('blindDates.diningStyles.business')}</option>
                <option value="romantic">{t('blindDates.diningStyles.romantic')}</option>
                <option value="social">{t('blindDates.diningStyles.social')}</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center space-x-2">
                <Heart className="h-4 w-4" />
                <span>{t('common.description')}</span>
              </div>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-md"
              rows={4}
              placeholder={t('blindDates.descriptionPlaceholder')}
              required
            ></textarea>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-pink-500 text-white px-6 py-3 rounded-md hover:bg-pink-600 transition flex items-center justify-center space-x-2"
          >
            {submitting ? (
              <>
                <Loader className="h-5 w-5 animate-spin" />
                <span>{t('blindDates.creating')}</span>
              </>
            ) : (
              <>
                <Heart className="h-5 w-5" />
                <span>{t('blindDates.createButton')}</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateBlindDate;